<script setup lang="ts">
import { computed, ref } from 'vue'

import { PagedList } from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-types'
import { useIncidentName } from '@desktop/views/Relation/Sports/composables'
import {
  getIncidentsGroupRepresentativeIncident,
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration/incidentsGroup'

import AddIncidentButton from './AddIncident/AddIncidentButton.vue'
import HistoryFilters from './HistoryFilters/HistoryFilters.vue'
import HistoryIncident from './HistoryIncident/HistoryIncident.vue'
import { Result } from './HistoryIncident/types'
import { useIncidentResult } from './HistoryIncident/useIncidentResult'
import HistoryNoFilteredIncidentsInfo from './HistoryInfo/HistoryNoFilteredIncidentsInfo.vue'
import HistoryNoIncidentsInfo from './HistoryInfo/HistoryNoIncidentsInfo.vue'

// composables
const { getIncidentName } = useIncidentName()

// state
const filters = ref<InstanceType<typeof HistoryFilters> | null>(null)

// computed
const incidents = computed(() => filters.value?.filteredIncidents || [])
const isAnyFilterActive = computed(() => !!filters.value?.isAnyFilterActive)
const isSameTimeFilterActive = computed(
  () => filters.value?.isSameTimeFilterActive || false,
)
const { getResult: _getResult } = useIncidentResult()

// methods
function getHistoryIncidentName(item: Incident | IncidentsGroup): string {
  return isIncidentsGroup(item) ? item.name : getIncidentName(item.incident_id)
}

function getIncidentKey(item: Incident | IncidentsGroup): number {
  return isIncidentsGroup(item)
    ? getIncidentsGroupRepresentativeIncident(item).id
    : item.id
}

function getResult(item: Incident | IncidentsGroup): Result | null {
  const incident = isIncidentsGroup(item)
    ? getIncidentsGroupRepresentativeIncident(item)
    : item

  return _getResult(incident)
}
</script>

<template>
  <div
    class="flex flex-col"
    data-testid="history-list"
  >
    <HistoryFilters ref="filters">
      <AddIncidentButton />
    </HistoryFilters>

    <PagedList
      class="flex-1 space-y-1 overflow-y-auto py-1"
      :items="incidents"
      reversed
    >
      <template #item="{ item, previousItem, nextItem }">
        <HistoryIncident
          :key="getIncidentKey(item)"
          :item
          :result="getResult(item)"
          :incidentName="getHistoryIncidentName(item)"
          :shouldShowChangeOrderButton="isSameTimeFilterActive"
          :nextIncident="nextItem"
          :previousIncident="previousItem"
        />
      </template>
      <template #empty>
        <div class="py-8">
          <HistoryNoFilteredIncidentsInfo v-if="isAnyFilterActive" />
          <HistoryNoIncidentsInfo v-else />
        </div>
      </template>
    </PagedList>
  </div>
</template>
